import {useParams} from "react-router";
import React, {useCallback, useEffect} from "react";
import {toRelayId, useLazyQuery$} from "../../graphqlsupport";
import {gql} from "@apollo/client";
import {useObservable} from "rxsupport";
import {Referral, ReferralStatus} from "../../referral";
import {Pane} from "evergreen-ui";
import {NewOfferFormForClientPlacement} from "./NewOfferFormForClientPlacement";
import {LoadingSpinner, showToast} from "common";
import {OfferReferralDetail} from "../../providerapp/component/OfferReferralDetail";
import {ErrorDialog} from "common/component/ErrorDialog";
import {BehaviorSubject} from "rxjs";
import {useGoToReferralListPageForPlacement} from "../../placement/routes";
import {ClientProviderPreference} from "../../placement/domains";

type Params = {
    id: string; // referral id
}

export function InternalPlacementSubmitOfferPage() {
    let {id} = useParams<Params>();

    return (
        <div className='h-screen placement-provider-offer-page-bg'>
            <OfferPage referralGuid={id}/>
        </div>
    )
}

const REFERRAL_BY_RELAY_ID_QUERY = gql`
query Referral($id: ID!) {
  clientPreferences: activeExcludedProviderPrefByReferral(id: $id) {
    sfProviderId
    clientPreference
    preferenceReason
  }
  referral: providerReferralForNewOfferInternal(id: $id) {
    id
    guid
    caseNumber
    clientName
    serviceTypes
    referralConsiderations
    status
    placementType
    location
    clientAge
    serviceLocations
    serviceLocationsParaprofessional
    serviceLocationsMidLevel
    serviceLocationsHighLevel
    serviceLocationsGroupCare
    requiredLanguage
    directCareHours
    midLevelHours
    highLevelHours
    groupCareHours
    authDirectCareHours
    authMidLevelHours
    authHighLevelHours
    authGroupCareHours
    authDirectCareBeneficialHours
    authMidLevelBeneficialHours
    authHighLevelBeneficialHours
    authGroupCareBeneficialHours
    careMediatedMidHours
    careMediatedHighHours
      careMediatedMidMinHours
      careMediatedHighMinHours
    practDirectBeneficialHours
    practMidBeneficialHours
    practHighBeneficialHours
    practGroupBeneficialHours
    practDirectOptimalHours
    practMidOptimalHours
    practHighOptimalHours
    practGroupOptimalHours
    authTherapyTreatmentUnitsPer
    authTherapyTreatmentUnitType
    authTherapyTreatmentUnit
    scheduleNotes
    county
  }
}`;

type ReferralByRelayIdInput = {
    id: string;
};
type ReferralByRelayIdResult = {
    referral: Referral;
    clientPreferences: ClientProviderPreference[];
};

const OfferPage = ({referralGuid}: { referralGuid: string }) => {
    const {
        isRunning$,
        execute,
        data,
        error,
    } = useLazyQuery$<ReferralByRelayIdInput, ReferralByRelayIdResult>(
        REFERRAL_BY_RELAY_ID_QUERY, {
            id: toRelayId(referralGuid ?? '', 'Referral')
        });

    useEffect(() => {
        execute();
    }, [execute]);

    const goToReferralPage = useGoToReferralListPageForPlacement();
    const referralNumber = data?.referral?.caseNumber ?? '';

    const successCallback = useCallback(() => {
        showToast.success(`Offer for referral ${referralNumber} was submitted successfully by Client Placement team`);
        goToReferralPage();
    }, [referralNumber, goToReferralPage]);

    const loading = useObservable(isRunning$, false);

    return (
        <div>
            {loading && <LoadingSpinner centerScreen/>}
            {!loading && !error && !data && <Pane display='flex' className="portal-provider-content-container"/>}
            {!loading && !error && data && data.referral.status === ReferralStatus.Matched &&
                <Pane display='flex' className="portal-provider-content-container">
                    <OfferReferralDetail referral={data.referral}/>
                    <Pane className={"flex-grow"}>
                        <Pane className="max-h-full h-full box-border portal-provider-offer-form-container flex flex-col overflow-y-scroll">
                            <ErrorDialog message$={new BehaviorSubject<string | null>("Referral already matched")}
                                         closeCallback={() => {goToReferralPage()}}/>
                        </Pane>
                    </Pane>
                </Pane>
            }
            {!loading && !error && data && data.referral.status !== ReferralStatus.Matched &&
                <Pane display='flex' className="portal-provider-content-container">
                    <OfferReferralDetail referral={data.referral}/>

                    <Pane className="flex-grow">
                        <NewOfferFormForClientPlacement referral={data.referral}
                                                        excludedProviderSfIdsFromPref={data.clientPreferences.map(p => p.sfProviderId)}
                                                        successCallback={successCallback}/>
                    </Pane>
                </Pane>
            }
        </div>
    )
}